import { z } from 'zod'
import { publicIdSchema, utcTimestampSchema } from './primitives'

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** The four form slots an admin may build for a competition. */
export const FORM_TYPE_VALUES = [
  'registration_form',
  'team_form',
  'submission_form',
  'judge_conflict_form',
] as const
export const formTypeSchema = z.enum(FORM_TYPE_VALUES)
export type FormType = z.infer<typeof formTypeSchema>

export const FORM_STATUS_VALUES = ['active', 'archived'] as const
export const formStatusSchema = z.enum(FORM_STATUS_VALUES)
export type FormStatus = z.infer<typeof formStatusSchema>

/**
 * `draft` may have its fields edited. `published` is the version currently in
 * force and is immutable — editing it clones a new `draft` instead of
 * mutating it, which is what lets a historical submission always be
 * redisplayed against the schema that was live when it was made (Invariant
 * #7). `superseded` is a `published` version a later one has replaced.
 */
export const FORM_VERSION_STATUS_VALUES = ['draft', 'published', 'superseded'] as const
export const formVersionStatusSchema = z.enum(FORM_VERSION_STATUS_VALUES)
export type FormVersionStatus = z.infer<typeof formVersionStatusSchema>

export const FORM_FIELD_TYPE_VALUES = [
  'short_text',
  'long_text',
  'rich_text',
  'number',
  'email',
  'phone',
  'url',
  'date',
  'datetime',
  'radio',
  'checkbox',
  'select',
  'multi_select',
  'boolean',
  'file',
  'image',
  'pdf',
  'zip',
  'heading',
  'description',
  'separator',
] as const
export const formFieldTypeSchema = z.enum(FORM_FIELD_TYPE_VALUES)
export type FormFieldType = z.infer<typeof formFieldTypeSchema>

/** Layout only — never carries an answer value and never appears in submitted answers. */
export const PRESENTATIONAL_FIELD_TYPES: readonly FormFieldType[] = [
  'heading',
  'description',
  'separator',
]

/** Answers as a stored-file reference. Upload wiring itself arrives in Phase 5-6. */
export const FILE_FIELD_TYPES: readonly FormFieldType[] = ['file', 'image', 'pdf', 'zip']

// ---------------------------------------------------------------------------
// Field key, validation/options/visibility JSON shapes
// ---------------------------------------------------------------------------

export const fieldKeySchema = z
  .string()
  .min(1)
  .max(120)
  .regex(
    /^[a-z][a-z0-9_]*$/,
    'Must start with a lowercase letter and contain only lowercase letters, digits and underscores.',
  )

/** Mirrors the `validation_json` example in `02-database-er-schema.md`. */
export const fieldValidationRulesSchema = z
  .strictObject({
    minLength: z.int().min(0).optional(),
    maxLength: z.int().min(0).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    pattern: z.string().max(500).optional(),
    allowedExtensions: z.array(z.string().trim().min(1).max(20)).max(50).optional(),
    maxFileSize: z.int().min(1).optional(),
  })
  .nullable()

export type FieldValidationRules = z.infer<typeof fieldValidationRulesSchema>

export const fieldOptionSchema = z.strictObject({
  value: z.string().min(1).max(200),
  label: z.string().min(1).max(200),
})
export type FieldOption = z.infer<typeof fieldOptionSchema>

export const fieldOptionsSchema = z.array(fieldOptionSchema).max(200).nullable()

export const VISIBILITY_CONDITION_OPERATOR_VALUES = ['equals', 'not_equals'] as const
export const visibilityConditionOperatorSchema = z.enum(VISIBILITY_CONDITION_OPERATOR_VALUES)

export const visibilityConditionSchema = z.strictObject({
  fieldKey: fieldKeySchema,
  operator: visibilityConditionOperatorSchema,
  value: z.union([z.string(), z.number(), z.boolean()]),
})
export type VisibilityCondition = z.infer<typeof visibilityConditionSchema>

export const VISIBILITY_LOGIC_OPERATOR_VALUES = ['AND', 'OR'] as const

/** `null` means always visible. */
export const fieldVisibilitySchema = z
  .strictObject({
    operator: z.enum(VISIBILITY_LOGIC_OPERATOR_VALUES),
    conditions: z.array(visibilityConditionSchema).min(1).max(20),
  })
  .nullable()
export type FieldVisibility = z.infer<typeof fieldVisibilitySchema>

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

export const formSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  type: formTypeSchema,
  name: z.string().trim().min(1).max(200),
  description: z.string().max(2000).nullable(),
  status: formStatusSchema,
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})
export type Form = z.infer<typeof formSchema>

export const formDtoSchema = formSchema.omit({ competitionId: true })
export type FormDto = z.infer<typeof formDtoSchema>

export const createFormRequestSchema = z.strictObject({
  type: formTypeSchema,
  name: z.string().trim().min(1).max(200),
  description: z.string().max(2000).nullable().default(null),
})
export type CreateFormRequest = z.infer<typeof createFormRequestSchema>

export const updateFormRequestSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    status: formStatusSchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided.',
  })
export type UpdateFormRequest = z.infer<typeof updateFormRequestSchema>

// ---------------------------------------------------------------------------
// Form version
// ---------------------------------------------------------------------------

export const formVersionSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  formId: publicIdSchema,
  versionNumber: z.int().min(1),
  status: formVersionStatusSchema,
  publishedAt: utcTimestampSchema.nullable(),
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})
export type FormVersion = z.infer<typeof formVersionSchema>

export const formVersionDtoSchema = formVersionSchema.omit({ competitionId: true, formId: true })
export type FormVersionDto = z.infer<typeof formVersionDtoSchema>

/**
 * The form plus its two version pointers: `currentVersion` is what a caller
 * outside the builder would see (the `published` version, if any), and
 * `draftVersion` is what the field editor is currently targeting — `null`
 * only in the instant between form creation and its first field write, which
 * `resolveDraftVersion` never actually leaves open.
 */
export const formDetailDtoSchema = formDtoSchema.extend({
  currentVersion: formVersionDtoSchema.nullable(),
  draftVersion: formVersionDtoSchema.nullable(),
})
export type FormDetailDto = z.infer<typeof formDetailDtoSchema>

// ---------------------------------------------------------------------------
// Form field
// ---------------------------------------------------------------------------

export const formFieldSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  formVersionId: publicIdSchema,
  key: fieldKeySchema,
  label: z.string().trim().min(1).max(200),
  description: z.string().max(2000).nullable(),
  type: formFieldTypeSchema,
  required: z.boolean(),
  placeholder: z.string().max(200).nullable(),
  defaultValue: z.string().max(2000).nullable(),
  validation: fieldValidationRulesSchema,
  options: fieldOptionsSchema,
  visibility: fieldVisibilitySchema,
  /**
   * PR-275/UX-297 (Submission Section Grouping Spec Delta, 2026-08-30).
   * `null` means this field is not part of any named section — the
   * pre-existing, still-default behavior (one status row per field). Two or
   * more fields on the same `formVersionId` sharing the exact same non-null
   * `sectionLabel` string are grouped under one visual heading in the
   * Submission Workspace; see `deriveSubmissionSections` in
   * `apps/web/src/features/submissions/derive-submission-sections.ts`.
   */
  sectionLabel: z.string().trim().min(1).max(120).nullable(),
  sortOrder: z.int().min(0).max(10_000),
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})
export type FormField = z.infer<typeof formFieldSchema>

export const formFieldDtoSchema = formFieldSchema.omit({
  competitionId: true,
  formVersionId: true,
  updatedAt: true,
})
export type FormFieldDto = z.infer<typeof formFieldDtoSchema>

const fieldWriteShape = {
  key: fieldKeySchema,
  label: z.string().trim().min(1).max(200),
  description: z.string().max(2000).nullable().default(null),
  type: formFieldTypeSchema,
  required: z.boolean().default(false),
  placeholder: z.string().max(200).nullable().default(null),
  defaultValue: z.string().max(2000).nullable().default(null),
  validation: fieldValidationRulesSchema.default(null),
  options: fieldOptionsSchema.default(null),
  visibility: fieldVisibilitySchema.default(null),
  sectionLabel: z.string().trim().min(1).max(120).nullable().default(null),
  sortOrder: z.int().min(0).max(10_000).default(0),
}

export const createFormFieldRequestSchema = z.strictObject(fieldWriteShape)
export type CreateFormFieldRequest = z.infer<typeof createFormFieldRequestSchema>

export const updateFormFieldRequestSchema = z
  .strictObject({
    key: fieldKeySchema.optional(),
    label: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    type: formFieldTypeSchema.optional(),
    required: z.boolean().optional(),
    placeholder: z.string().max(200).nullable().optional(),
    defaultValue: z.string().max(2000).nullable().optional(),
    validation: fieldValidationRulesSchema.optional(),
    options: fieldOptionsSchema.optional(),
    visibility: fieldVisibilitySchema.optional(),
    sectionLabel: z.string().trim().min(1).max(120).nullable().optional(),
    sortOrder: z.int().min(0).max(10_000).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided.',
  })
export type UpdateFormFieldRequest = z.infer<typeof updateFormFieldRequestSchema>

export const reorderFormFieldsRequestSchema = z.strictObject({
  fieldIds: z.array(publicIdSchema).min(1).max(500),
})
export type ReorderFormFieldsRequest = z.infer<typeof reorderFormFieldsRequestSchema>

// ---------------------------------------------------------------------------
// Answer validation request/response (used by the `/validate` route)
// ---------------------------------------------------------------------------

export const validateFormAnswersRequestSchema = z.strictObject({
  answers: z.record(z.string(), z.unknown()),
})
export type ValidateFormAnswersRequest = z.infer<typeof validateFormAnswersRequestSchema>

export interface ValidateFormAnswersResult {
  valid: boolean
  errors: Record<string, string[]>
}

// ---------------------------------------------------------------------------
// Field-mutation responses
// ---------------------------------------------------------------------------

/**
 * Every field-mutation endpoint reports which version the change actually
 * landed on and whether that version was just cloned from the published one
 * — the client-visible half of Invariant #7's "editing a published form
 * clones a new version" rule.
 */
export interface FormFieldMutationDto {
  field: FormFieldDto
  versionId: string
  cloned: boolean
}

export interface FormFieldListMutationDto {
  fields: FormFieldDto[]
  versionId: string
  cloned: boolean
}
